import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { Check, X, MessageCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminNegotiations = () => {
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const navigate = useNavigate();
  const adminEmail = getAdminEmail();
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState({});

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/negotiations/pending?admin_email=${encodeURIComponent(adminEmail)}`);
      setNegotiations(res.data.negotiations || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to load negotiations', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminEmail) fetchPending(); }, [adminEmail]);

  const setReason = (id, value) => setReasons(prev => ({ ...prev, [id]: value }));

  const approveShipper = async (id) => {
    try {
      await axios.post(`${API}/admin/negotiations/${id}/approve-shipper?admin_email=${encodeURIComponent(adminEmail)}`);
      toast({ title: 'Approved', description: 'Carrier has been notified.' });
      fetchPending();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const rejectShipper = async (id) => {
    const reason = (reasons[id] || '').trim();
    if (reason.length < 5) {
      toast({ title: 'Reason required', description: 'Please provide at least 5 characters explaining the rejection.', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/admin/negotiations/${id}/reject-shipper?admin_email=${encodeURIComponent(adminEmail)}`, { reason });
      toast({ title: 'Rejected', description: 'Shipper has been notified.' });
      fetchPending();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const approveCarrierReply = async (id) => {
    try {
      await axios.post(`${API}/admin/negotiations/${id}/approve-carrier-reply?admin_email=${encodeURIComponent(adminEmail)}`);
      toast({ title: 'Approved', description: 'Shipper has been notified.' });
      fetchPending();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const rejectCarrierReply = async (id) => {
    const reason = (reasons[id] || '').trim();
    if (reason.length < 5) {
      toast({ title: 'Reason required', description: 'Please provide at least 5 characters.', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/admin/negotiations/${id}/reject-carrier-reply?admin_email=${encodeURIComponent(adminEmail)}`, { reason });
      toast({ title: 'Rejected', description: 'Carrier has been notified.' });
      fetchPending();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const shipperPending = negotiations.filter(n => n.status === 'pending_admin_approval');
  const carrierPending = negotiations.filter(n => n.status === 'carrier_reply_pending_admin');

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-[#1e3a5f] text-white py-6">
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                size="sm"
                className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f]"
                data-testid="admin-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <MessageCircle className="w-6 h-6" /> Quote Negotiations
              </h1>
            </div>
            <p className="text-sm text-gray-200">Approve or reject shipper messages and carrier replies</p>
          </div>
          <Button onClick={fetchPending} variant="outline" className="bg-white/10 text-white border-white/40 hover:bg-white hover:text-[#1e3a5f]" data-testid="refresh-btn">Refresh</Button>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <p className="text-center text-gray-600">Loading...</p>
        ) : negotiations.length === 0 ? (
          <Card className="p-12 text-center">
            <Check className="w-16 h-16 mx-auto text-green-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">All caught up!</h3>
            <p className="text-gray-600">No negotiations pending review</p>
          </Card>
        ) : (
          <>
            {shipperPending.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#1e3a5f] mb-3">Shipper Messages Awaiting Approval ({shipperPending.length})</h2>
                <div className="space-y-4">
                  {shipperPending.map(n => (
                    <Card key={n.id} className="p-4" data-testid={`admin-shipper-neg-${n.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm text-gray-600">
                          {n.load_summary && (
                            <p><strong>Route:</strong> {n.load_summary.pickup_location} → {n.load_summary.dropoff_location} · {n.load_summary.cargo_type}</p>
                          )}
                          <p><strong>Quote:</strong> ${n.quote_amount} · <strong>Shipper:</strong> {n.shipper_email} · <strong>Carrier:</strong> {n.carrier_email}</p>
                        </div>
                        <p className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-3">
                        <p className="text-sm"><strong>Message:</strong> {n.shipper_message}</p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Button
                          onClick={() => approveShipper(n.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-shipper-neg-${n.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve &amp; Send to Carrier
                        </Button>
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Rejection reason (min 5 chars)"
                            value={reasons[n.id] || ''}
                            onChange={(e) => setReason(n.id, e.target.value)}
                            rows={1}
                            className="text-sm"
                            data-testid={`reject-reason-${n.id}`}
                          />
                          <Button
                            onClick={() => rejectShipper(n.id)}
                            variant="outline"
                            className="w-full border-red-600 text-red-600 hover:bg-red-50"
                            data-testid={`reject-shipper-neg-${n.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {carrierPending.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-[#1e3a5f] mb-3">Carrier Replies Awaiting Approval ({carrierPending.length})</h2>
                <div className="space-y-4">
                  {carrierPending.map(n => (
                    <Card key={n.id} className="p-4" data-testid={`admin-carrier-reply-${n.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm text-gray-600">
                          {n.load_summary && (
                            <p><strong>Route:</strong> {n.load_summary.pickup_location} → {n.load_summary.dropoff_location} · {n.load_summary.cargo_type}</p>
                          )}
                          <p><strong>Quote:</strong> ${n.quote_amount} · <strong>Shipper:</strong> {n.shipper_email} · <strong>Carrier:</strong> {n.carrier_email}</p>
                        </div>
                        <p className="text-xs text-gray-500">{n.carrier_replied_at ? new Date(n.carrier_replied_at).toLocaleString() : ''}</p>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-2">
                        <p className="text-sm"><strong>Original Shipper Message:</strong> {n.shipper_message}</p>
                      </div>
                      <div className={`p-3 mb-3 rounded ${n.carrier_decision === 'accepted' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-amber-50 border-l-4 border-amber-500'}`}>
                        <p className="text-sm"><strong>Carrier ({(n.carrier_decision || '').toUpperCase()}):</strong> {n.carrier_reply_message}</p>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <Button
                          onClick={() => approveCarrierReply(n.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-carrier-reply-${n.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve &amp; Send to Shipper
                        </Button>
                        <div className="space-y-2">
                          <Textarea
                            placeholder="Rejection reason (min 5 chars)"
                            value={reasons[n.id] || ''}
                            onChange={(e) => setReason(n.id, e.target.value)}
                            rows={1}
                            className="text-sm"
                            data-testid={`reject-carrier-reason-${n.id}`}
                          />
                          <Button
                            onClick={() => rejectCarrierReply(n.id)}
                            variant="outline"
                            className="w-full border-red-600 text-red-600 hover:bg-red-50"
                            data-testid={`reject-carrier-reply-${n.id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AdminNegotiations;
